import { supabase } from '../lib/supabase';
import { Mission, SupabaseMissionResponse } from '../types/missions';

export const missionsService = {
  async createMission(mission: Omit<Mission, 'id' | 'createdAt' | 'updatedAt'>) {
    // Créer la mission
    const { data: missionData, error: missionError } = await supabase
      .from('missions')
      .insert({
        name: mission.name,
        description: mission.description,
        set_id: mission.setId,
        is_active: mission.isActive
      })
      .select()
      .single();

    if (missionError) {
      console.error('Erreur lors de la création de la mission:', missionError);
      throw missionError;
    }

    // Créer les groupes de cartes
    for (const group of mission.requiredCardGroups) {
      const { data: groupData, error: groupError } = await supabase
        .from('mission_card_groups')
        .insert({ mission_id: missionData.id })
        .select()
        .single();

      if (groupError) throw groupError;

      // Créer les variantes de cartes pour ce groupe
      const cardVariants = group.cards.map(card => ({
        group_id: groupData.id,
        card_id: card.cardId
      }));

      const { error: variantsError } = await supabase
        .from('mission_card_variants')
        .insert(cardVariants);

      if (variantsError) throw variantsError;
    }

    // Créer les récompenses
    if (mission.rewards && mission.rewards.length > 0) {
      const missionRewards = mission.rewards.map(reward => ({
        mission_id: missionData.id,
        reward_id: reward.id.toString(),
        quantity: reward.quantity
      }));

      const { error: rewardsError } = await supabase
        .from('mission_rewards')
        .insert(missionRewards);

      if (rewardsError) {
        console.error('Erreur lors de la création des récompenses:', rewardsError);
        throw rewardsError;
      }
    }

    return this.getMissionById(missionData.id);
  },

  async updateMission(id: string, mission: Partial<Mission>) {
    const { error: missionError } = await supabase
      .from('missions')
      .update({
        name: mission.name,
        description: mission.description,
        set_id: mission.setId,
        is_active: mission.isActive
      })
      .eq('id', id);

    if (missionError) throw missionError;

    if (mission.requiredCardGroups) {
      // Supprimer les anciens groupes (cascade supprimera aussi les variantes)
      await supabase
        .from('mission_card_groups')
        .delete()
        .eq('mission_id', id);

      // Créer les nouveaux groupes
      for (const group of mission.requiredCardGroups) {
        const { data: groupData, error: groupError } = await supabase
          .from('mission_card_groups')
          .insert({ mission_id: id })
          .select()
          .single();

        if (groupError) throw groupError;

        const cardVariants = group.cards.map(card => ({
          group_id: groupData.id,
          card_id: card.cardId
        }));

        const { error: variantsError } = await supabase
          .from('mission_card_variants')
          .insert(cardVariants);

        if (variantsError) throw variantsError;
      }
    }

    if (mission.rewards) {
      // Mettre à jour les récompenses
      await supabase
        .from('mission_rewards')
        .delete()
        .eq('mission_id', id);

      const missionRewards = mission.rewards.map(reward => ({
        mission_id: id,
        reward_id: reward.id.toString(),
        quantity: reward.quantity
      }));

      const { error: rewardsError } = await supabase
        .from('mission_rewards')
        .insert(missionRewards);

      if (rewardsError) throw rewardsError;
    }

    return this.getMissionById(id);
  },

  async deleteMission(id: string) {
    const { error } = await supabase
      .from('missions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getMissions() {
    const { data, error } = await supabase
      .from('missions')
      .select(`
        *,
        set:set_id (*),
        mission_card_groups (
          id,
          mission_card_variants (
            card_id,
            card:card_id (*)
          )
        ),
        mission_rewards (
          reward:reward_id (*)
        )
      `);

    if (error) throw error;

    // Transformer les données pour correspondre à notre type Mission
    return (data as SupabaseMissionResponse[]).map(mission => ({
      id: mission.id,
      name: mission.name,
      description: mission.description,
      setId: mission.set_id,
      set: mission.set,
      isActive: mission.is_active,
      createdAt: mission.created_at,
      updatedAt: mission.updated_at,
      requiredCardGroups: mission.mission_card_groups.map(group => ({
        id: group.id,
        cards: group.mission_card_variants.map(variant => ({
          cardId: variant.card_id
        }))
      })),
      rewards: mission.mission_rewards.map(mr => ({
        id: mr.reward.id,
        quantity: mr.quantity
      }))
    }));
  },

  async getMissionById(id: string) {
    const { data, error } = await supabase
      .from('missions')
      .select(`
        *,
        set:set_id (*),
        mission_card_groups (
          id,
          mission_card_variants (
            card_id,
            card:card_id (*)
          )
        ),
        mission_rewards (
          quantity,
          reward:reward_id (*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    const mission = data as SupabaseMissionResponse;
    
    // Transformer les données pour correspondre à notre type Mission
    return {
      id: mission.id,
      name: mission.name,
      description: mission.description,
      setId: mission.set_id,
      set: mission.set,
      isActive: mission.is_active,
      createdAt: mission.created_at,
      updatedAt: mission.updated_at,
      requiredCardGroups: mission.mission_card_groups.map(group => ({
        id: group.id,
        cards: group.mission_card_variants.map(variant => ({
          cardId: variant.card_id
        }))
      })),
      rewards: mission.mission_rewards.map(mr => ({
        id: mr.reward.id,
        quantity: mr.quantity
      }))
    };
  }
}; 